import React, { useCallback, useEffect, useRef, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Autocomplete, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import WorkspaceEmptyState from "assets/images/WorkspaceEmptyState.png";
import { CharAutocomplete } from "components/Characteristics/Deal-form-drawer/CharAutocomplete";
import { CreateAssetClass, CreateSubAssetClass } from "components/Prompts";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { currenciesArray } from "constants/index";
import { useActionHook, usePrompt } from "hooks";
import { IApiServiceError, useAccountSelector, useDealActions } from "store";
import { IAssetClass } from "store/types";
import pickFiles from "utils/pick-files";

import { AiLoadingState } from "../../modals/Deal-create/components/AiLoadingState";
import { EditInvestmentModal } from "../EditInvestmentModal";

const ACCEPTED_TYPES = ".pdf";

export interface InvestmentData {
  dealName: string;
  assetClass: IAssetClass | null;
  subAssetClass: IAssetClass | null;
  currency: string;
  geography: string;
  file?: File;
  extractedData?: {
    characteristics: Array<{ characteristic_id: number; value: string; name?: string; currency?: string | null }>;
  };
}

interface InvestmentStepProps {
  step: number;
  totalSteps: number;
  initialInvestments?: InvestmentData[];
  onComplete: (investments: InvestmentData[]) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export const InvestmentStep: React.FC<InvestmentStepProps> = ({
  step,
  totalSteps,
  initialInvestments = [],
  onComplete,
  onNext,
  onBack,
  onSkip
}) => {
  const [flowPath, setFlowPath] = useState<"file-upload" | "manual" | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [investments, setInvestments] = useState<InvestmentData[]>(initialInvestments);
  const [currentInvestment, setCurrentInvestment] = useState<InvestmentData>({
    dealName: "",
    assetClass: null,
    subAssetClass: null,
    currency: "USD",
    geography: ""
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [editingInvestmentIndex, setEditingInvestmentIndex] = useState<number | null>(null);

  const { allow_ai_deal_creation } = useAccountSelector() || {};
  const {
    getAssetClassesAction,
    getCharacteristicsAction,
    getCharacteristicsGeneralDataAction,
    extractDealFromFileAction
  } = useDealActions();
  const { data: assetClassesData = [], action: getAssetClasses } = useActionHook(getAssetClassesAction);
  const { data: characteristicsData = [] } = useActionHook(getCharacteristicsAction);
  const {
    data: generalCharacteristicsData = { characteristic_list: [], categories: [] },
    action: getGeneralCharacteristics
  } = useActionHook(getCharacteristicsGeneralDataAction);
  const {
    action: extractFromFile,
    loading: extracting,
    error: extractionError,
    resetError: resetExtractionError
  } = useActionHook(extractDealFromFileAction);

  const characteristicsList = generalCharacteristicsData?.characteristic_list.filter(
    (c) => c.name !== "Geography" && c.name !== "Currency"
  );
  const categories = generalCharacteristicsData?.categories || [];

  useEffect(() => {
    getAssetClasses();
    getGeneralCharacteristics();
  }, [getAssetClasses, getGeneralCharacteristics]);

  // Reset flow path when user has reached maximum investments
  // This ensures that clicking Back from ReadyStep shows the choice screen, not the form
  useEffect(() => {
    if (investments.length >= 3) {
      setFlowPath(null);
    }
  }, [investments.length]);

  const geographyCharacteristic = characteristicsData.find((c) => c.name === "Geography");
  const geographyCharacteristicId = geographyCharacteristic?.id || null;

  const openPrompt = usePrompt();
  const assetClassInputRef = useRef<HTMLInputElement>(null);
  const subAssetClassInputRef = useRef<HTMLInputElement>(null);
  const subAssetClassOptions = currentInvestment.assetClass?.sub_asset_class ?? [];

  const handleFileSelected = useCallback(
    async (file: File) => {
      setUploadedFile(file);
      setFlowPath("file-upload");

      try {
        const result = await extractFromFile(file);

        setCurrentInvestment((prev) => {
          const updates: Partial<InvestmentData> = { ...prev };

          if (result.deal_name) updates.dealName = result.deal_name;
          if (result.currency) updates.currency = result.currency;
          if (result.geography) updates.geography = result.geography;

          // Resolve asset_class_id → IAssetClass object
          if (result.asset_class_id) {
            const matched = assetClassesData.find((ac) => ac.id === result.asset_class_id);
            if (matched) {
              updates.assetClass = matched;
              // Resolve sub_asset_class_id within the matched parent
              if (result.sub_asset_class_id) {
                const sub = matched.sub_asset_class.find((s) => s.id === result.sub_asset_class_id);
                if (sub) updates.subAssetClass = sub;
              }
            }
          }

          // Store extracted characteristics data
          if (result.characteristics && result.characteristics.length > 0) {
            updates.extractedData = {
              characteristics: result.characteristics.map((char) => {
                const characteristic = characteristicsData.find((c) => c.id === char.characteristic_id);
                return {
                  ...char,
                  name: characteristic?.name
                };
              })
            };
          }

          return updates as InvestmentData;
        });
      } catch {
        // Error is captured by useActionHook's error state
      }
    },
    [extractFromFile, characteristicsData, assetClassesData]
  );

  const handleBrowse = useCallback(async () => {
    const fileList = await pickFiles({ accept: ACCEPTED_TYPES });
    if (!fileList?.length) return;
    handleFileSelected(fileList[0]);
  }, [handleFileSelected]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const dropped = e.dataTransfer.files?.[0];
      if (!dropped || !dropped.name.toLowerCase().endsWith(".pdf")) return;
      handleFileSelected(dropped);
    },
    [handleFileSelected]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleManual = () => {
    setFlowPath("manual");
  };

  const handleExtractionBack = useCallback(() => {
    resetExtractionError();
    setUploadedFile(null);
    setFlowPath(null);
  }, [resetExtractionError]);

  const handleClearFileData = () => {
    setUploadedFile(null);
    setFlowPath("manual");
    setCurrentInvestment({
      dealName: "",
      assetClass: null,
      subAssetClass: null,
      currency: "USD",
      geography: ""
    });
  };

  const selectAssetClass = (value: IAssetClass | null) => {
    setCurrentInvestment((prev) => ({
      ...prev,
      assetClass: value,
      subAssetClass: null
    }));
    if (fieldErrors.assetClass) {
      const { assetClass: _, ...rest } = fieldErrors;
      setFieldErrors(rest);
    }
  };

  const createAssetClassHandler = async () => {
    const inputValue = assetClassInputRef.current?.value || "";
    const result = await openPrompt({
      Component: CreateAssetClass,
      props: { initialValue: inputValue }
    });
    if (!result) return;
    await getAssetClasses();
    selectAssetClass(result);
  };

  const createSubAssetClassHandler = async () => {
    if (!currentInvestment.assetClass) return;

    const { id: parentId } = currentInvestment.assetClass;
    const inputValue = subAssetClassInputRef.current?.value || "";

    const result = await openPrompt({
      Component: CreateSubAssetClass,
      props: { initialValue: inputValue, parentId }
    });

    if (!result) return;

    await getAssetClasses();

    // Find the updated asset class after refresh
    const refreshedAssetClass = assetClassesData.find((ac) => ac.id === parentId);
    if (refreshedAssetClass) {
      setCurrentInvestment((prev) => ({ ...prev, assetClass: refreshedAssetClass, subAssetClass: result }));
    }
  };

  const handleAddInvestment = () => {
    const errors: Record<string, string> = {};
    if (!currentInvestment.dealName.trim()) errors.dealName = "Deal name is required";
    if (!currentInvestment.assetClass) errors.assetClass = "Asset class is required";
    if (!currentInvestment.currency) errors.currency = "Currency is required";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Include the file if it was uploaded (file-upload flow)
    const investmentToAdd = {
      ...currentInvestment,
      ...(flowPath === "file-upload" && uploadedFile ? { file: uploadedFile } : {})
    };

    const newInvestments = [...investments, investmentToAdd];
    setInvestments(newInvestments);
    onComplete(newInvestments);

    // If we've reached the maximum (3), show choice screen with maximum reached message
    if (newInvestments.length >= 3) {
      setFlowPath(null);
      return;
    }

    // Otherwise, clear form for next investment
    setCurrentInvestment({
      dealName: "",
      assetClass: null,
      subAssetClass: null,
      currency: "USD",
      geography: ""
    });
    setFieldErrors({});
    setUploadedFile(null);
    setFlowPath(null);
  };

  const handleRemoveInvestment = (index: number) => {
    setInvestments(investments.filter((_, i) => i !== index));
  };

  const handleSaveEditedInvestment = (updatedInvestment: InvestmentData) => {
    if (editingInvestmentIndex !== null) {
      const updatedInvestments = [...investments];
      updatedInvestments[editingInvestmentIndex] = updatedInvestment;
      setInvestments(updatedInvestments);
      setEditingInvestmentIndex(null);
    }
  };

  const handleContinue = () => {
    onComplete(investments);
    onNext();
  };

  const extractionErrorMessage = extractionError
    ? Object.values((extractionError as IApiServiceError)?.errors || {})
        .flat()
        .join(". ") ||
      (extractionError as IApiServiceError)?.message ||
      "Failed to extract data from file"
    : null;

  const canAddMore = investments.length < 3;
  const canContinue = investments.length > 0;
  const isFormValid = currentInvestment.dealName.trim() && currentInvestment.assetClass && currentInvestment.currency;
  const isFileUploadFlow = flowPath === "file-upload";

  // Render loading/extraction state
  const renderLoadingState = () => (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}
    >
      {/* Back button - top left */}
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIcon sx={{ fontSize: 14, color: "#26a6ae" }} />}
        sx={{
          position: "absolute",
          top: 24,
          left: 32,
          color: "#26a6ae",
          textTransform: "none",
          fontSize: "14px",
          minWidth: "auto",
          p: 0.5,
          "&:hover": {
            opacity: 0.8
          }
        }}
      >
        Back
      </Button>

      {/* Skip and Step counter - top right */}
      <Stack
        direction="row"
        gap="16px"
        alignItems="center"
        sx={{
          position: "absolute",
          top: 24,
          right: 32
        }}
      >
        <Button
          onClick={onSkip}
          sx={{
            color: "rgba(10, 49, 66, 0.6)",
            textTransform: "none",
            fontSize: "14px",
            minWidth: "auto",
            p: 0.5,
            "&:hover": {
              bgcolor: "transparent",
              color: "#071f35"
            }
          }}
        >
          Skip this step
        </Button>
        <Box
          sx={{
            width: "1px",
            height: "14px",
            bgcolor: "rgba(10, 49, 66, 0.15)"
          }}
        />
        <Box
          sx={{
            fontSize: "13px",
            color: "rgba(10, 49, 66, 0.5)",
            fontWeight: 500
          }}
        >
          Step {step} of {totalSteps}
        </Box>
      </Stack>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <AiLoadingState isLoading={extracting} error={extractionErrorMessage} onBack={handleExtractionBack} />
      </Box>
    </Box>
  );

  // Render choice screen
  const renderChoiceScreen = () => (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}
    >
      {/* Back button - top left */}
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIcon sx={{ fontSize: 14, color: "#26a6ae" }} />}
        sx={{
          position: "absolute",
          top: 24,
          left: 32,
          color: "#26a6ae",
          textTransform: "none",
          fontSize: "14px",
          minWidth: "auto",
          p: 0.5,
          "&:hover": {
            opacity: 0.8
          }
        }}
      >
        Back
      </Button>

      {/* Skip and Step counter - top right */}
      <Stack
        direction="row"
        gap="16px"
        alignItems="center"
        sx={{
          position: "absolute",
          top: 24,
          right: 32
        }}
      >
        <Button
          onClick={onSkip}
          sx={{
            color: "rgba(10, 49, 66, 0.6)",
            textTransform: "none",
            fontSize: "14px",
            minWidth: "auto",
            p: 0.5,
            "&:hover": {
              bgcolor: "transparent",
              color: "#071f35"
            }
          }}
        >
          Skip this step
        </Button>
        <Box
          sx={{
            width: "1px",
            height: "14px",
            bgcolor: "rgba(10, 49, 66, 0.15)"
          }}
        />
        <Box
          sx={{
            fontSize: "13px",
            color: "rgba(10, 49, 66, 0.5)",
            fontWeight: 500
          }}
        >
          Step {step} of {totalSteps}
        </Box>
      </Stack>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pt: 8,
          pb: 6,
          px: 3
        }}
      >
        <Box sx={{ maxWidth: "600px", width: "100%" }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: "28px",
              fontWeight: 500,
              color: "#0a3142",
              mb: 1,
              textAlign: "center"
            }}
          >
            Add your first investment
          </Typography>

          <Typography
            sx={{
              fontSize: "15px",
              color: "rgba(10, 49, 66, 0.6)",
              mb: 4,
              textAlign: "center"
            }}
          >
            {allow_ai_deal_creation ? "Upload a PDF or enter details manually" : "Enter investment details"}
          </Typography>

          {/* Added Investments */}
          {investments.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142", mb: 2 }}>
                Added Investments ({investments.length})
              </Typography>
              <Stack gap={2}>
                {investments.map((inv, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid #26a6ae",
                      borderRadius: "10px",
                      p: 2,
                      bgcolor: "#dbe8f5"
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#0a3142", mb: 1 }}>
                          {inv.dealName}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          <Box>
                            <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)" }}>
                              Asset Class
                            </Typography>
                            <Typography sx={{ fontSize: "14px", color: "#0a3142" }}>
                              {inv.assetClass?.account_alias || inv.assetClass?.name}
                            </Typography>
                          </Box>
                          {inv.subAssetClass && (
                            <Box>
                              <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)" }}>
                                Sub Asset Class
                              </Typography>
                              <Typography sx={{ fontSize: "14px", color: "#0a3142" }}>
                                {inv.subAssetClass.account_alias || inv.subAssetClass.name}
                              </Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)" }}>Currency</Typography>
                            <Typography sx={{ fontSize: "14px", color: "#0a3142" }}>{inv.currency}</Typography>
                          </Box>
                          {inv.geography && (
                            <Box>
                              <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)" }}>
                                Geography
                              </Typography>
                              <Typography sx={{ fontSize: "14px", color: "#0a3142" }}>{inv.geography}</Typography>
                            </Box>
                          )}
                          {(() => {
                            const filledChars =
                              inv.extractedData?.characteristics?.filter((c) => c.value && c.value.trim()) || [];
                            const maxDisplay = 2;
                            const displayChars = filledChars.slice(0, maxDisplay);
                            const remainingCount = filledChars.length - maxDisplay;

                            return (
                              <>
                                {displayChars.map((char, idx) => {
                                  const charDef = characteristicsList.find((c) => c.id === char.characteristic_id);
                                  const charName = char.name || charDef?.name || "Unknown";

                                  return (
                                    <Box key={idx}>
                                      <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.5)" }}>
                                        {charName}
                                      </Typography>
                                      <Typography sx={{ fontSize: "14px", color: "#0a3142" }}>
                                        {char.currency ? `${char.currency} ${char.value}` : char.value}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                                {remainingCount > 0 && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      px: 1.5,
                                      py: 0.5,
                                      bgcolor: "rgba(38, 166, 174, 0.1)",
                                      borderRadius: "12px",
                                      alignSelf: "center"
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#26a6ae" }}>
                                      +{remainingCount} more
                                    </Typography>
                                  </Box>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      </Box>
                      <Stack direction="row" gap={1}>
                        <Button
                          onClick={() => setEditingInvestmentIndex(index)}
                          sx={{
                            minWidth: "auto",
                            color: "#071f35",
                            fontSize: "13px",
                            textTransform: "none",
                            "&:hover": { bgcolor: "rgba(10, 49, 66, 0.04)" }
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleRemoveInvestment(index)}
                          sx={{
                            minWidth: "auto",
                            color: "rgba(10, 49, 66, 0.6)",
                            fontSize: "13px",
                            textTransform: "none",
                            "&:hover": { bgcolor: "rgba(10, 49, 66, 0.04)" }
                          }}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Stack gap={3}>
            {/* Illustration - Only show when no investments */}
            {!investments.length && allow_ai_deal_creation && (
              <Stack alignItems="center">
                <Box sx={{ height: "118px", width: "188px", overflow: "hidden" }}>
                  <Box
                    component="img"
                    src={WorkspaceEmptyState}
                    sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
                  />
                </Box>
              </Stack>
            )}

            {/* Maximum reached message */}
            {!canAddMore && (
              <Box
                sx={{
                  bgcolor: "#e8f6f7",
                  borderRadius: "8px",
                  p: 2,
                  textAlign: "center"
                }}
              >
                <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#26a6ae", mb: 0.5 }}>
                  Maximum investments reached
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "rgba(10, 49, 66, 0.6)" }}>
                  You&apos;ve added 3 investments. Click Continue to proceed to the next step.
                </Typography>
              </Box>
            )}

            {/* Drag & drop file zone - Only show when can add more and AI deal creation is enabled */}
            {canAddMore && allow_ai_deal_creation && (
              <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                sx={{
                  bgcolor: "#fafcff",
                  border: "1px dashed",
                  borderColor: isDragging ? "#26a6ae" : "rgba(10, 49, 66, 0.2)",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  height: "173px",
                  cursor: "pointer",
                  transition: "border-color 0.2s",
                  "&:hover": {
                    borderColor: "#26a6ae",
                    bgcolor: "#ebf6f7"
                  },
                  ...(isDragging && { bgcolor: "#ebf6f7" })
                }}
                onClick={handleBrowse}
              >
                <Stack alignItems="center">
                  <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#0a3142", lineHeight: "24px" }}>
                    Drag & drop or{" "}
                    <Box component="span" sx={{ color: "#26a6ae", textDecoration: "underline" }}>
                      browse
                    </Box>
                  </Typography>
                  <Stack direction="row" alignItems="center" gap="2px">
                    <Typography
                      component="span"
                      sx={{ fontSize: "14px", color: "rgba(10, 49, 66, 0.6)", lineHeight: "16px" }}
                    >
                      Upload investment PDF to extract information
                    </Typography>
                    <MaterialTooltip
                      title="Upload any financial document and we'll extract the investment details automatically"
                      isShow
                      placement="right"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: "rgba(10, 49, 66, 0.6)", cursor: "pointer" }} />
                    </MaterialTooltip>
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* Or divider - Only show when can add more and AI deal creation is enabled */}
            {canAddMore && allow_ai_deal_creation && (
              <Stack alignItems="center">
                <Typography sx={{ fontSize: "14px", color: "rgba(10, 49, 66, 0.6)", lineHeight: "16px" }}>
                  Or
                </Typography>
              </Stack>
            )}

            {/* Manual Entry card - Only show when can add more */}
            {canAddMore && (
              <Stack
                direction="row"
                onClick={handleManual}
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  border: "1px solid rgba(10, 49, 66, 0.12)",
                  padding: "10px",
                  gap: "10px",
                  height: "64px",
                  cursor: "pointer",
                  "&:hover": { borderColor: "#26a6ae" }
                }}
              >
                <Stack
                  sx={{
                    bgcolor: "#f6f8fc",
                    borderRadius: "8px",
                    width: 44,
                    height: 44,
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0
                  }}
                >
                  <EditNoteOutlinedIcon sx={{ fontSize: 20, color: "#071f35" }} />
                </Stack>
                <Stack sx={{ gap: "1px", justifyContent: "center", minWidth: 0 }}>
                  <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#0a3142", lineHeight: "20px" }}>
                    Manual Entry
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "rgba(10, 49, 66, 0.6)", lineHeight: "16px" }}>
                    Start from scratch by using the form
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>

          {/* Bottom Actions */}
          <Stack direction="column" gap="12px" alignItems="center" justifyContent="center" sx={{ mt: 4 }}>
            {canContinue && (
              <Button
                variant="contained"
                onClick={handleContinue}
                sx={{
                  width: "100px",
                  bgcolor: "#071f35",
                  boxShadow: "none",
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#0f2d47"
                  }
                }}
              >
                Continue
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  // Render form (manual or after file extraction)
  const renderForm = () => (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}
    >
      {/* Back button - top left */}
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIcon sx={{ fontSize: 14, color: "#26a6ae" }} />}
        sx={{
          position: "absolute",
          top: 24,
          left: 32,
          color: "#26a6ae",
          textTransform: "none",
          fontSize: "14px",
          minWidth: "auto",
          p: 0.5,
          "&:hover": {
            opacity: 0.8
          }
        }}
      >
        Back
      </Button>

      {/* Skip and Step counter - top right */}
      <Stack
        direction="row"
        gap="16px"
        alignItems="center"
        sx={{
          position: "absolute",
          top: 24,
          right: 32
        }}
      >
        <Button
          onClick={onSkip}
          sx={{
            color: "rgba(10, 49, 66, 0.6)",
            textTransform: "none",
            fontSize: "14px",
            minWidth: "auto",
            p: 0.5,
            "&:hover": {
              bgcolor: "transparent",
              color: "#071f35"
            }
          }}
        >
          Skip this step
        </Button>
        <Box
          sx={{
            width: "1px",
            height: "14px",
            bgcolor: "rgba(10, 49, 66, 0.15)"
          }}
        />
        <Box
          sx={{
            fontSize: "13px",
            color: "rgba(10, 49, 66, 0.5)",
            fontWeight: 500
          }}
        >
          Step {step} of {totalSteps}
        </Box>
      </Stack>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pt: 8,
          pb: 6,
          px: 3
        }}
      >
        <Box sx={{ maxWidth: "720px", width: "100%" }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: "28px",
              fontWeight: 500,
              color: "#0a3142",
              mb: 1
            }}
          >
            {!investments.length ? "Add your first investment" : "Add another investment"}
          </Typography>

          <Typography
            sx={{
              fontSize: "15px",
              color: "rgba(10, 49, 66, 0.6)",
              mb: 4
            }}
          >
            {!investments.length
              ? isFileUploadFlow
                ? "Upload up to 3 fund documents — Altius will extract financial data automatically. More files = richer analytics from day one."
                : "Add up to 3 investments to get started. You can add more later."
              : `You can add up to ${3 - investments.length} more investment${3 - investments.length !== 1 ? "s" : ""}.`}
          </Typography>

          {/* Extraction complete banner — file upload flow only */}
          {isFileUploadFlow && uploadedFile && (
            <Stack
              sx={{
                bgcolor: "#f6f8fc",
                borderRadius: "8px",
                width: "100%",
                mb: 3
              }}
            >
              <Stack direction="row" alignItems="center" gap="10px" sx={{ padding: "10px" }}>
                <Stack sx={{ gap: "2px", flex: 1 }}>
                  <Typography sx={{ fontSize: "15px", lineHeight: "20px", color: "#071f35" }}>
                    Extraction complete:{" "}
                    <Typography
                      component="span"
                      sx={{ fontSize: "15px", fontWeight: 500, lineHeight: "20px", color: "#071f35" }}
                    >
                      {uploadedFile.name}
                    </Typography>
                  </Typography>
                  <Typography sx={{ fontSize: "15px", lineHeight: "20px", color: "#496682" }}>
                    The extracted data is added to the fields of the form below.
                  </Typography>
                </Stack>
                <Button
                  variant="outlined"
                  onClick={handleClearFileData}
                  sx={{
                    textTransform: "none",
                    borderRadius: "32px",
                    fontSize: "12px",
                    lineHeight: "16px",
                    color: "#071f35",
                    borderColor: "#7b92a9",
                    bgcolor: "white",
                    px: "8px",
                    py: "4px",
                    maxWidth: "240px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    "&:hover": { borderColor: "#496682", bgcolor: "#f6f8fc" }
                  }}
                >
                  Clear file and data
                </Button>
              </Stack>
            </Stack>
          )}

          {/* Investment Form */}
          <Stack gap={2} sx={{ mb: 4 }}>
            {/* Deal Name */}
            <TextField
              label="Deal Name"
              placeholder="Enter Deal Name"
              required
              fullWidth
              value={currentInvestment.dealName}
              onChange={(e) => {
                setCurrentInvestment((prev) => ({ ...prev, dealName: e.target.value }));
                if (fieldErrors.dealName) {
                  const { dealName: _, ...rest } = fieldErrors;
                  setFieldErrors(rest);
                }
              }}
              error={!!fieldErrors.dealName}
              helperText={fieldErrors.dealName}
            />

            {/* Asset Class + Sub Asset Class */}
            <Stack direction="row" gap={2}>
              <Autocomplete
                fullWidth
                onChange={(_, value) => selectAssetClass(value)}
                getOptionLabel={(option) => option.account_alias || option.name}
                options={assetClassesData}
                value={currentInvestment.assetClass}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Typography>{option.account_alias || option.name}</Typography>
                  </li>
                )}
                slotProps={{
                  paper: {
                    component: ({ children }) => (
                      <Paper
                        sx={{
                          position: "absolute",
                          backgroundColor: "white",
                          width: "100%",
                          zIndex: "10",
                          borderRadius: "10px",
                          "& .MuiAutocomplete-option:hover": { backgroundColor: "#edf1f9" }
                        }}
                      >
                        {children}
                        <Button
                          onMouseDown={(e) => {
                            e.preventDefault();
                            createAssetClassHandler();
                          }}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            height: "50px",
                            backgroundColor: "#fff",
                            boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                            borderRadius: "0 0 10px 10px",
                            zIndex: 100
                          }}
                          fullWidth
                        >
                          Add Asset Class
                        </Button>
                      </Paper>
                    )
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={assetClassInputRef}
                    label="Asset Class"
                    placeholder="Select Asset Class"
                    error={!!fieldErrors.assetClass}
                    helperText={fieldErrors.assetClass}
                    required
                  />
                )}
              />

              <Autocomplete
                fullWidth
                onChange={(_, value) => setCurrentInvestment((prev) => ({ ...prev, subAssetClass: value }))}
                options={subAssetClassOptions}
                value={currentInvestment.subAssetClass}
                getOptionLabel={(option) => option.account_alias || option.name}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Typography>{option.account_alias || option.name}</Typography>
                  </li>
                )}
                slotProps={{
                  paper: {
                    component: ({ children }) => (
                      <Paper
                        sx={{
                          position: "absolute",
                          backgroundColor: "white",
                          width: "100%",
                          zIndex: "10",
                          borderRadius: "10px",
                          "& .MuiAutocomplete-option:hover": { backgroundColor: "#edf1f9" }
                        }}
                      >
                        {children}
                        <Button
                          disabled={!currentInvestment.assetClass}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            createSubAssetClassHandler();
                          }}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            height: "50px",
                            backgroundColor: "#fff",
                            boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                            borderRadius: "0 0 10px 10px",
                            zIndex: 100
                          }}
                          fullWidth
                        >
                          Add Sub Asset Class
                        </Button>
                      </Paper>
                    )
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sub Asset Class"
                    placeholder="Choose Sub Asset Class"
                    inputRef={subAssetClassInputRef}
                  />
                )}
              />
            </Stack>

            {/* Currency + Geography */}
            <Stack direction="row" gap={2}>
              <Autocomplete
                fullWidth
                value={currentInvestment.currency}
                onChange={(_, value) => {
                  setCurrentInvestment((prev) => ({ ...prev, currency: value || "" }));
                  if (fieldErrors.currency) {
                    const { currency: _, ...rest } = fieldErrors;
                    setFieldErrors(rest);
                  }
                }}
                options={currenciesArray}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Currency"
                    placeholder="Select Currency"
                    required
                    error={!!fieldErrors.currency}
                    helperText={fieldErrors.currency}
                  />
                )}
              />
              {geographyCharacteristicId ? (
                <CharAutocomplete
                  charName="Geography"
                  placeholder="Search or Enter Geography"
                  characteristicId={geographyCharacteristicId}
                  value={currentInvestment.geography}
                  onChange={(value) => setCurrentInvestment((prev) => ({ ...prev, geography: value }))}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Geography"
                  placeholder="Enter Geography"
                  value={currentInvestment.geography}
                  onChange={(e) => setCurrentInvestment((prev) => ({ ...prev, geography: e.target.value }))}
                />
              )}
            </Stack>

            <Stack direction="row" justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleAddInvestment}
                disabled={!isFormValid}
                sx={{
                  bgcolor: "#071f35",
                  boxShadow: "none",
                  border: "none",
                  maxWidth: "160px",
                  color: "white",
                  fontSize: "15px",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "#0f2d47"
                  },
                  "&:disabled": {
                    bgcolor: "rgba(10, 49, 66, 0.12)",
                    color: "rgba(255, 255, 255, 0.6)"
                  }
                }}
              >
                Add Investment
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  // Main return with modal always present
  return (
    <>
      {extracting || extractionErrorMessage ? renderLoadingState() : !flowPath ? renderChoiceScreen() : renderForm()}

      {/* Edit Investment Modal - Always rendered */}
      <EditInvestmentModal
        open={editingInvestmentIndex !== null}
        investment={editingInvestmentIndex !== null ? investments[editingInvestmentIndex] : null}
        assetClassesData={assetClassesData}
        characteristicsList={characteristicsList}
        categories={categories}
        onClose={() => setEditingInvestmentIndex(null)}
        onSave={handleSaveEditedInvestment}
      />
    </>
  );
};
